import { BrandLogoMark } from "@v1/ui/brand-logo-mark";
import {
  Bot,
  Calendar,
  Folder,
  Inbox,
  KeyRound,
  MessageSquare,
  Phone,
  RefreshCw,
  SquareCheckBig,
} from "lucide-react";
import { useTranslations } from "next-intl";
import type { CSSProperties, ReactNode } from "react";
import { H } from "../hero-tokens";

/**
 * SECTION 4 — the "resolution" diagram revealed as the chaos windows collapse.
 * Left card: the hausverwalter.ai hub-and-spoke radial (central dark logo + 8
 * satellite tool icons on a dashed ellipse). Center: animated sync pills + ring
 * + refresh icon. Right card: a generic "Externes Tool" skeleton. Captions sit
 * below each column. 1:1 with the prototype.
 */

const HUB_CARD_SHADOW =
  "0 46px 92px -40px rgba(11,15,25,0.34), 0 14px 32px -16px rgba(11,15,25,0.16)";
/** Shared by both diagram cards on the scaled mobile stage. */
const ERP_CARD_SHADOW = "0 36px 72px -38px rgba(11,15,25,0.2)";

/** The 8 satellite endpoints (cx,cy in the 430×236 SVG) + their lucide icon. */
const SATELLITES: readonly {
  readonly x: number;
  readonly y: number;
  readonly icon: ReactNode;
}[] = [
  { x: 215, y: 26, icon: null }, // KI — rendered specially (accent ring)
  { x: 337, y: 53, icon: <Inbox size={18} strokeWidth={1.7} /> },
  { x: 387, y: 118, icon: <Phone size={18} strokeWidth={1.7} /> },
  { x: 337, y: 183, icon: <Folder size={18} strokeWidth={1.7} /> },
  { x: 215, y: 210, icon: <MessageSquare size={18} strokeWidth={1.7} /> },
  { x: 93, y: 183, icon: <SquareCheckBig size={18} strokeWidth={1.7} /> },
  { x: 43, y: 118, icon: <Calendar size={18} strokeWidth={1.7} /> },
  { x: 93, y: 53, icon: <KeyRound size={18} strokeWidth={1.7} /> },
];

function satelliteBox(x: number, y: number, accent: boolean): CSSProperties {
  return {
    position: "absolute",
    left: x,
    top: y,
    transform: "translate(-50%,-50%)",
    width: accent ? 46 : 38,
    height: accent ? 46 : 38,
    borderRadius: accent ? 13 : 11,
    background: accent ? H.accentSoft : "#fff",
    border: accent ? `1.5px solid ${H.accent}` : "1px solid rgba(11,15,25,0.1)",
    boxShadow: accent
      ? "0 10px 22px -6px rgba(0,128,112,0.38)"
      : "0 5px 13px -6px rgba(11,15,25,0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: accent ? H.accent : "rgba(11,15,25,0.55)",
    zIndex: accent ? 3 : 2,
  };
}

function CardHeaderDot(): ReactNode {
  return (
    <span style={{ display: "flex", gap: 5 }}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "#E1E4E9",
          }}
        />
      ))}
    </span>
  );
}

function HubSpokeCard(props: { readonly compactShadow?: boolean }): ReactNode {
  const t = useTranslations("marketing.scrollStageDiagram");
  return (
    <div
      style={{
        flex: 1,
        maxWidth: 462,
        height: 312,
        background: "#fff",
        border: "1px solid rgba(11,15,25,0.08)",
        borderRadius: 18,
        overflow: "hidden",
        boxShadow:
          props.compactShadow === true ? ERP_CARD_SHADOW : HUB_CARD_SHADOW,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 9,
          padding: "13px 16px",
          borderBottom: "1px solid rgba(11,15,25,0.06)",
          background: "rgba(252,252,251,0.85)",
        }}
      >
        <BrandLogoMark width={26} height={26} variant="dark" />
        <span style={{ fontSize: 13.5, fontWeight: 700, color: "#0B0F19" }}>
          {t.rich("hausverwalterAiWortmarke", {
            dim: (chunks) => (
              <span style={{ color: "rgba(11,15,25,0.38)", fontWeight: 600 }}>
                {chunks}
              </span>
            ),
          })}
        </span>
        <span
          style={{
            marginLeft: "auto",
            fontFamily: H.label,
            fontSize: 9,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "rgba(11,15,25,0.38)",
          }}
        >
          {t("allOne")}
        </span>
      </div>
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "relative",
            width: 430,
            height: 236,
            flexShrink: 0,
          }}
        >
          <svg
            viewBox="0 0 430 236"
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              overflow: "visible",
            }}
          >
            <ellipse
              cx="215"
              cy="118"
              rx="172"
              ry="92"
              fill="none"
              style={{ stroke: H.accent }}
              strokeOpacity="0.13"
              strokeWidth="1"
              strokeDasharray="1.5 6"
            />
            {SATELLITES.map((s, i) => (
              <line
                key={i}
                x1="215"
                y1="118"
                x2={s.x}
                y2={s.y}
                style={{ stroke: H.accent }}
                strokeOpacity="0.13"
                strokeWidth="1"
              />
            ))}
          </svg>
          {/* KI satellite (accent ring) */}
          <div style={satelliteBox(215, 26, true)}>
            <Bot size={22} strokeWidth={1.8} />
          </div>
          {/* The 7 plain satellites */}
          {SATELLITES.slice(1).map((s, i) => (
            <div key={i} style={satelliteBox(s.x, s.y, false)}>
              {s.icon}
            </div>
          ))}
          {/* Central dark logo hub */}
          <div
            style={{
              position: "absolute",
              left: 215,
              top: 118,
              transform: "translate(-50%,-50%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 4,
            }}
          >
            <span
              style={{
                position: "absolute",
                width: 108,
                height: 108,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(0,128,112,0.2), transparent 68%)",
              }}
            />
            <span
              style={{
                position: "relative",
                display: "flex",
                borderRadius: 15,
                boxShadow: "0 16px 34px -10px rgba(11,15,25,0.5)",
              }}
            >
              <BrandLogoMark width={56} height={56} variant="dark" />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function SyncConnector(): ReactNode {
  const pill = (
    color: string,
    size: number,
    delay?: number,
  ): CSSProperties => ({
    position: "absolute",
    top: "50%",
    marginTop: -(size / 2),
    width: size,
    height: size,
    borderRadius: "50%",
    background: color,
    ...(delay !== undefined ? { animationDelay: `${delay}s` } : null),
  });
  return (
    <div
      style={{
        width: 116,
        flexShrink: 0,
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: 8,
          right: 8,
          top: "50%",
          height: 2,
          marginTop: -1,
          background: `linear-gradient(90deg, rgba(0,128,112,0.12), ${H.accent} 50%, rgba(0,128,112,0.12))`,
          borderRadius: 2,
        }}
      />
      <div className="mkhero-syncflow-r" style={pill(H.accent, 6)} />
      <div className="mkhero-syncflow-r" style={pill(H.accent, 6, 0.9)} />
      <div className="mkhero-syncflow-l" style={pill("#34D3B5", 5, 0.5)} />
      <div
        style={{
          position: "relative",
          width: 44,
          height: 44,
          borderRadius: "50%",
          background: H.accent,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 12px 26px -8px rgba(0,128,112,0.55)",
          zIndex: 2,
        }}
      >
        <span
          className="mkhero-syncring"
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            background: "rgba(0,128,112,0.35)",
          }}
        />
        <RefreshCw
          size={20}
          strokeWidth={2.2}
          style={{ position: "relative" }}
        />
      </div>
    </div>
  );
}

function ExternalToolCard(): ReactNode {
  const t = useTranslations("marketing.scrollStageDiagram");
  const rowWidths = [150, 120, 168, 104, 138];
  return (
    <div
      style={{
        flex: 1,
        maxWidth: 462,
        height: 312,
        background: "#fff",
        border: "1px solid #ECEEF1",
        borderRadius: 18,
        overflow: "hidden",
        boxShadow: ERP_CARD_SHADOW,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "13px 16px",
          borderBottom: "1px solid #F1F2F4",
          background: "#FAFBFC",
        }}
      >
        <CardHeaderDot />
        <span
          style={{
            width: 20,
            height: 20,
            borderRadius: 6,
            background: "#E2E5EA",
            marginLeft: 3,
            flexShrink: 0,
          }}
        />
        <div
          style={{
            height: 9,
            width: 88,
            background: "#E2E5EA",
            borderRadius: 3,
          }}
        />
        <span
          style={{
            marginLeft: "auto",
            fontFamily: H.label,
            fontSize: 9,
            letterSpacing: "0.08em",
            color: "rgba(11,15,25,0.3)",
          }}
        >
          {t("externesTool")}
        </span>
      </div>
      <div
        style={{
          padding: "14px 16px",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 8,
            paddingBottom: 11,
            borderBottom: "1px solid #F1F2F4",
          }}
        >
          <div
            style={{
              height: 26,
              width: 92,
              background: "#EEF0F3",
              borderRadius: 7,
            }}
          />
          <div
            style={{
              height: 26,
              width: 62,
              background: "#F1F3F5",
              borderRadius: 7,
            }}
          />
          <div
            style={{
              marginLeft: "auto",
              height: 26,
              width: 78,
              background: "#EEF0F3",
              borderRadius: 7,
            }}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", marginTop: 6 }}>
          {rowWidths.map((w, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "9px 2px",
                borderBottom: "1px solid #F5F6F8",
              }}
            >
              <div
                style={{
                  height: 8,
                  width: w,
                  background: "#E7EAED",
                  borderRadius: 3,
                }}
              />
              <div style={{ flex: 1 }} />
              <div
                style={{
                  height: 8,
                  width: 52,
                  background: "#E7EAED",
                  borderRadius: 3,
                }}
              />
              <div
                style={{
                  height: 8,
                  width: 38,
                  background: "#EDEFF2",
                  borderRadius: 3,
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** The full resolution diagram block (positioned absolutely by the caller). */
export function ResolutionDiagram(props: {
  readonly compactShadow?: boolean;
  /**
   * Compensates for parent ResponsiveScale on mobile — same idea as
   * {@link ScrollStageCanvas}'s headlineScale.
   */
  readonly captionScale?: number;
}): ReactNode {
  const t = useTranslations("marketing.scrollStageDiagram");
  const captionScale = props.captionScale ?? 1;
  const titleSize = 24 * captionScale;
  const subtitleSize = 15 * captionScale;
  const captionGap = 4 * captionScale;
  const syncLabelSize = 10 * captionScale;

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          justifyContent: "center",
        }}
      >
        <HubSpokeCard compactShadow={props.compactShadow} />
        <SyncConnector />
        <ExternalToolCard />
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          marginTop: 15 * captionScale,
        }}
      >
        <div style={{ flex: 1, maxWidth: 462, textAlign: "center" }}>
          <div
            style={{ fontSize: titleSize, fontWeight: 600, color: "#0B0F19" }}
          >
            {t("hausverwalterAi2")}
          </div>
          <div
            style={{
              fontSize: subtitleSize,
              color: "rgba(11,15,25,0.5)",
              marginTop: captionGap,
            }}
          >
            {t("alleWerkzeugeSoftware")}
          </div>
        </div>
        <div
          style={{
            width: 116,
            flexShrink: 0,
            display: "flex",
            justifyContent: "center",
            paddingTop: 2 * captionScale,
          }}
        >
          <div
            style={{
              fontFamily: H.label,
              fontSize: syncLabelSize,
              letterSpacing: "0.03em",
              textTransform: "uppercase",
              color: H.accent,
              paddingLeft: "0.03em",
              whiteSpace: "nowrap",
            }}
          >
            {t("stammdatenSync")}
          </div>
        </div>
        <div style={{ flex: 1, maxWidth: 462, textAlign: "center" }}>
          <div
            style={{
              fontSize: titleSize,
              fontWeight: 600,
              color: "rgba(11,15,25,0.72)",
            }}
          >
            {t("erpSystem")}
          </div>
          <div
            style={{
              fontSize: subtitleSize,
              color: "rgba(11,15,25,0.5)",
              marginTop: captionGap,
            }}
          >
            {t("bleibtKennst")}
          </div>
        </div>
      </div>
    </>
  );
}
